/**
 * What the spend figures say, where a runner can reach them.
 *
 * 🔴 **This is the one copy module in the SPA allowed to use the word "spend", and the
 * reason is worth stating because three other modules are forbidden it.**
 * `campaign-copy.test.ts` refuses `spend|spent|limit|cap|remaining` in the campaign
 * screens, and `line-item-copy.test.ts` does the same for theirs, because those screens
 * have **no spend figure**: an X ad account carries no `amount_spent`, and every
 * sentence built on one said something nobody could check. Here the figure exists and
 * is X's own `billed_charge_local_micro` from its BILLING metric group, so naming it is
 * a report. The guards stay exactly as they are; this module is not added to their file
 * sets, and its own cases below carry the rules that do apply.
 *
 * 🔴 **And the figure is provisional, which is the most important thing on the screen.**
 * The owner set a ceiling of 20 USD; a number that can still move is a number a ceiling
 * cannot be checked against without saying so. X states two horizons that disagree (3
 * days and 14 days) and names the direction in only one place, so both are quoted and
 * neither is chosen here.
 *
 * Nothing here divides or rounds: `lib/money.ts` is the only place in this SPA that
 * turns micros into an amount, and it truncates rather than rounds for the ceiling's
 * sake.
 */
import { microMoney } from "./money.ts";
import { type BilledRowState, STATS_MAX_IDS, type TotalFigure } from "./stats.ts";

/** The column head, with the window in it rather than beside it. */
export const SPEND_LABEL = "Spend 7d";

/**
 * The bare word, for the Overview's analytics (2026-10-22), whose periods are named
 * beside the figure rather than in it — the word lives here, in the module allowed it.
 */
export const SPEND = "Spend";

/**
 * One row's figure.
 *
 * 🔴 **"Nothing" is not "0.00", and "unreadable" is neither.** X returns `null` where
 * it holds nothing, and says an all-null answer likely means the entity did not serve
 * in the window — so this prints an em dash and the screen's note says what X says.
 * Writing "0.00 EUR" there would be this console asserting a measured zero, which is a
 * different claim from "X reported nothing" even where the two happen to coincide.
 */
export function spendCell(figure: TotalFigure, currency: string | null): string {
	switch (figure.kind) {
		case "value":
			return microMoney(figure.micro, currency);
		case "nothing":
			return "—";
		case "unreadable":
			return `? (${String(figure.buckets)} buckets)`;
	}
}

/**
 * One row of the spend column, worded.
 *
 * 🔴 **Five labels, and no two states share one.** They used to live as three string
 * literals inside two screens, which is where "not asked" came to stand for both "the
 * id was never in the request" and "X did not mention it" — and a screen cannot be
 * tested for a sentence it invents. The worst of the three was not a label at all but a
 * claim about a mechanism, which is the thing this module exists to keep out of
 * components.
 *
 * 🔴 **`reading…` and `—` are deliberately different.** `—` belongs to a figure X
 * reported as nothing; `reading…` is this console not having asked yet. The dash for
 * both would be the same collapse the figures themselves refuse.
 */
export function billedCellText(state: BilledRowState, currency: string | null): string {
	switch (state.kind) {
		case "not-asked":
			return "not asked";
		case "read-failed":
			return "read failed";
		case "unread":
			return "reading…";
		case "absent":
			// X answered and did not mention this id. Its own words, because X documents
			// the row as present with null metrics when an entity did not serve — so an
			// absent row is a shape X does not document, and naming it is all that can
			// honestly be done with it.
			return "no row from X";
		case "figure":
			return spendCell(state.figure, currency);
	}
}

/**
 * The read's own timestamp, said plainly.
 *
 * No "updated" and no "live": this screen does not refresh itself, so a figure on it is
 * as old as the moment named here and says so.
 */
export function readAtText(readAt: string): string {
	return `read ${readAt}`;
}

/**
 * 🔴 The four things a figure on this screen is not, each one X's own statement.
 *
 * They sit under the table rather than in every row, as `NO_SERVABLE` does on the
 * line-item list: a sentence repeated per row is a sentence nobody reads, and these
 * four are properties of the figure rather than of one campaign.
 */
export const SPEND_NOTES = [
	"This figure is not final. X calls billed spend an estimate for up to 3 days after it is returned, and says it processes billing data for up to 14 days from the event — two different horizons, both X's own words. It also says the figure can decrease after 24 hours, through credits for overspend and for billable events it judges junk. Every other analytics metric X locks after 24 hours; this is the stated exception.",
	"The window ends at the top of the current hour and that end is exclusive, so the hour in progress is not counted. X's own Ads UI treats the end as inclusive, which is why a figure here and a figure in ads.x.com over what look like the same days are not expected to match.",
	"Only the ALL_ON_TWITTER placement is counted — X accepts one placement per request and tells callers to ask for SPOTLIGHT and TREND separately before comparing with its UI. This console creates line items on ALL_ON_TWITTER only, so anything placed elsewhere in this account is not in these numbers.",
	"X's analytics answer carries no currency. The code shown comes from this account's funding instrument, which is a different object; where this console does not hold one, the raw micro figure is shown instead of a guessed currency.",
] as const;

/**
 * 🔴 Said when X's twenty-id ceiling left rows out, and said with the number.
 *
 * Without it the rows past the twentieth show nothing in the spend column and look
 * exactly like rows that spent nothing — a correct figure against the wrong rows, which
 * is the mistake this sentence exists to make impossible rather than unlikely.
 *
 * «among the rows read», not «on this page»: the cut is over every row read (`idsForStats`
 * on the screen's whole list), and «page» is the table's own (`NEXT_PAGE`).
 */
export function coveredText(cut: number): string | null {
	if (cut <= 0) return null;
	return `Spend is shown for the first ${String(STATS_MAX_IDS)} rows only: X answers about at most that many entities in one request, and ${String(cut)} more ${cut === 1 ? "is" : "are"} among the rows read. Those rows say "not asked" rather than a figure — and a row this console did ask about, which X answered without mentioning, says "no row from X". Neither means nothing was spent.`;
}

/**
 * When the figures could not be read at all.
 *
 * 🔴 It lives here rather than inline on the screen for a reason that is this module's
 * whole premise: `campaign-copy.test.ts` refuses the word "spend" in
 * `pages/Campaigns.tsx`, because that screen used to claim amounts it could not know.
 * A sentence about a failed read is not such a claim — but the guard cannot tell a
 * report from a claim, and it should not have to. So the sentence moves to the module
 * that is allowed the word and is tested for what it may say, and the screen keeps none
 * of its own.
 */
export const SPEND_READ_FAILED = "The figures from X could not be read.";

/**
 * 🔴 Under the campaign card's figures: which four, from where, and what X's two click
 * metrics are. The route asks X for the BILLING and ENGAGEMENT metric groups and hands on
 * five fields — `billedChargeLocalMicro`, `impressions`, `engagements`, `urlClicks`,
 * `clicks` (docs/API-CONTRACT.md, GET /api/v1/stats; the last two since twads#174). The
 * card shows URL clicks under X's own name for the metric and quotes X's definition;
 * `clicks` is read and not shown. Until 2026-10-21 this constant was `THREE_FIGURES` and
 * said «Clicks are not in that answer, so none is shown» — true then: the route did not
 * read them. It lives here, not in the card's module, for the reason the header gives:
 * this is the module allowed the word.
 *
 * 🔴 «URL clicks», not «link clicks»: the reference defines `url_clicks` as clicks on the
 * link or Website Card and derives its own «Link Clicks» from three per-placement metrics
 * the synchronous endpoint's table does not list, and no live reading has yet shown which
 * of X's two click metrics the «link clicks» in Ads Manager is. The name follows the
 * metric until one has.
 */
export const FIGURES_NOTE =
	"Four figures over the last seven days, from the same read as the Spend column on the Campaigns screen: spend, impressions, engagements and URL clicks — the two metric groups the stats route asks X for (BILLING, ENGAGEMENT). URL clicks is X's url_clicks: «Total clicks on the link or Website Card in an ad, including earned.» X also reports clicks — «Total number of clicks, including favorites and other engagements» — which is read and not shown, and whether the «link clicks» in Ads Manager is either of them has not been checked against a live reading. A dash is X holding nothing for the window, or a metric X did not send; 0 or 0.00 is a figure X reported; «reading…» and «read failed» mean this console has no answer.";

/** The line under the figures: when the read was, what window, which placement. */
export function figuresReadText(answer: {
	readAt: string;
	window: { start: string; end: string };
	placement: string;
}): string {
	return `${readAtText(answer.readAt)} · ${answer.window.start} to ${answer.window.end} · ${answer.placement}`;
}

/** Said once, where someone would look for a refresh button. */
export const NO_AUTO_REFRESH =
	"Nothing here refreshes on its own. Reload the page to ask X again — and a figure read twice in the same hour asks X the same question, because the window is built from whole hours.";
